import { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import type { DocumentData, Query, QueryDocumentSnapshot, Unsubscribe } from 'firebase/firestore';

/**
 * Many hooks across the app independently open onSnapshot listeners for the
 * exact same Firestore document/query (e.g. users/{uid}, or eventChats where
 * memberIds array-contains uid) because several screens/hooks need the same
 * data at once. This registry lets N consumers share a single underlying
 * Firestore listener keyed by a caller-supplied string key, replaying the
 * most recent snapshot to late subscribers so their loading/data timing is
 * unchanged from having their own listener.
 */

interface SharedEntry<T> {
  unsubscribe: Unsubscribe;
  dataListeners: Set<(data: T) => void>;
  errorListeners: Set<(error: Error) => void>;
  lastData: T | undefined;
  hasData: boolean;
  lastError: Error | undefined;
}

const registry = new Map<string, SharedEntry<any>>();

export function subscribeShared<T>(
  key: string,
  start: (onData: (data: T) => void, onError: (error: Error) => void) => Unsubscribe,
  onData: (data: T) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  let entry = registry.get(key) as SharedEntry<T> | undefined;

  if (!entry) {
    const created: SharedEntry<T> = {
      unsubscribe: () => {},
      dataListeners: new Set(),
      errorListeners: new Set(),
      lastData: undefined,
      hasData: false,
      lastError: undefined,
    };
    created.unsubscribe = start(
      data => {
        created.lastData = data;
        created.hasData = true;
        created.lastError = undefined;
        created.dataListeners.forEach(listener => listener(data));
      },
      error => {
        created.lastError = error;
        created.errorListeners.forEach(listener => listener(error));
      },
    );
    if (__DEV__) {
      console.log(`[Perf][Listeners] shared Firestore listener OPENED (registry size=${registry.size + 1})`, { key });
    }
    registry.set(key, created);
    entry = created;
  }

  entry.dataListeners.add(onData);
  if (onError) entry.errorListeners.add(onError);

  if (entry.hasData) {
    onData(entry.lastData as T);
  } else if (entry.lastError && onError) {
    onError(entry.lastError);
  }

  return () => {
    const current = registry.get(key) as SharedEntry<T> | undefined;
    if (!current) return;
    current.dataListeners.delete(onData);
    if (onError) current.errorListeners.delete(onError);
    if (current.dataListeners.size === 0 && current.errorListeners.size === 0) {
      current.unsubscribe();
      registry.delete(key);
      if (__DEV__) {
        console.log(`[Perf][Listeners] shared Firestore listener CLOSED (registry size=${registry.size})`, { key });
      }
    }
  };
}

/** Shared onSnapshot for a Firestore query, deduped by `key`. Returns the raw docs array. */
export function useSharedQueryDocs<T extends DocumentData = DocumentData>(
  key: string | null,
  buildQuery: () => Query<T>,
): { docs: QueryDocumentSnapshot<T>[]; loaded: boolean; error: Error | null } {
  const [state, setState] = useState<{ docs: QueryDocumentSnapshot<T>[]; loaded: boolean; error: Error | null }>({
    docs: [],
    loaded: false,
    error: null,
  });

  useEffect(() => {
    if (!key) {
      setState({ docs: [], loaded: true, error: null });
      return undefined;
    }
    setState(prev => (prev.loaded ? { ...prev, loaded: false, error: null } : prev));
    const unsubscribe = subscribeShared<QueryDocumentSnapshot<T>[]>(
      key,
      (onData, onError) => onSnapshot(buildQuery(), snap => onData(snap.docs), onError),
      docs => setState({ docs, loaded: true, error: null }),
      error => setState(prev => ({ ...prev, loaded: true, error })),
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
